/**
 * Coordinate conversion between the embedpdf viewer and NDSign field space.
 *
 * Fields are stored in PDF points with a TOP-LEFT origin
 * (x grows right, y grows down, relative to the page's top-left corner).
 *
 * Screen position of a point inside a page:
 *   screenX = (pageOrigin.x + field.x) * scale - scrollLeft + offset.x
 *   screenY = (pageOrigin.y + field.y) * scale - scrollTop  + offset.y
 */

export interface PageLayoutLike {
  pageNumber: number;
  x: number;
  y: number;
  width: number;
  height: number;
  rotatedWidth: number;
  rotatedHeight: number;
}

export interface VirtualItemLike {
  x: number;
  y: number;
  width?: number;
  pageLayouts: PageLayoutLike[];
}

export interface LayoutLike {
  virtualItems: VirtualItemLike[];
  totalContentSize?: { width: number; height: number };
}

export function contentWidth(layout: LayoutLike): number {
  if (layout.totalContentSize?.width) return layout.totalContentSize.width;
  return layout.virtualItems.reduce((max, item) => Math.max(max, item.width ?? 0), 0);
}

export function contentHeight(layout: LayoutLike): number {
  if (layout.totalContentSize?.height) return layout.totalContentSize.height;
  return layout.virtualItems.reduce((sum, item) => sum + (item.pageLayouts[0]?.rotatedHeight ?? 0), 0);
}

export interface Point {
  x: number;
  y: number;
}

export interface RectLike {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface ScreenRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

export function pageOrigin(layout: LayoutLike, pageNumber: number): RectLike | null {
  for (const item of layout.virtualItems) {
    for (const pl of item.pageLayouts) {
      if (pl.pageNumber === pageNumber) {
        return {
          x: item.x + pl.x,
          y: item.y + pl.y,
          w: pl.rotatedWidth,
          h: pl.rotatedHeight,
        };
      }
    }
  }
  return null;
}

export function allPageOrigins(layout: LayoutLike): Record<number, RectLike> {
  const result: Record<number, RectLike> = {};
  for (const item of layout.virtualItems) {
    for (const pl of item.pageLayouts) {
      result[pl.pageNumber] = {
        x: item.x + pl.x,
        y: item.y + pl.y,
        w: pl.rotatedWidth,
        h: pl.rotatedHeight,
      };
    }
  }
  return result;
}

export function fieldToScreenRect(
  page: RectLike,
  scale: number,
  scroll: Point,
  offset: Point,
  field: RectLike
): ScreenRect {
  return {
    left: (page.x + field.x) * scale - scroll.x + offset.x,
    top: (page.y + field.y) * scale - scroll.y + offset.y,
    width: field.w * scale,
    height: field.h * scale,
  };
}

export function screenPointToPage(
  pages: Record<number, RectLike>,
  scale: number,
  scroll: Point,
  offset: Point,
  point: Point
): { page: number; x: number; y: number } | null {
  const contentX = (point.x - offset.x + scroll.x) / scale;
  const contentY = (point.y - offset.y + scroll.y) / scale;
  for (const pageNumber of Object.keys(pages).map(Number).sort((a, b) => a - b)) {
    const p = pages[pageNumber];
    if (contentX >= p.x && contentX <= p.x + p.w && contentY >= p.y && contentY <= p.y + p.h) {
      return { page: pageNumber, x: contentX - p.x, y: contentY - p.y };
    }
  }
  return null;
}

export function clampFieldToPage(page: RectLike, field: RectLike): RectLike {
  const w = Math.min(field.w, page.w);
  const h = Math.min(field.h, page.h);
  return {
    w,
    h,
    x: Math.max(0, Math.min(field.x, page.w - w)),
    y: Math.max(0, Math.min(field.y, page.h - h)),
  };
}

export function screenDeltaToPageDelta(scale: number, dx: number, dy: number): Point {
  return { x: dx / scale, y: dy / scale };
}
